import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from './auth.service';
import { SystemErrorModalService } from './system-error-modal.service';
import { NetworkOfflineModalService } from './network-offline-modal.service';
import { catchError, throwError } from 'rxjs';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const systemErrorModal = inject(SystemErrorModalService);
  const networkOfflineModal = inject(NetworkOfflineModalService);
  const t = auth.tokens();

  const setHeaders: Record<string, string> = {};

  if (t?.access_token) {
    const scheme = (t as any).token_type || 'Bearer';
    setHeaders['Authorization'] = `${scheme} ${t.access_token}`; // usar token_type del backend
  }

  // Bypass del warning de ngrok para XHR/fetch
  if (req.url.includes('ngrok-free.app')) {
    setHeaders['ngrok-skip-browser-warning'] = 'true';
  }

  const cloned = Object.keys(setHeaders).length
    ? req.clone({ setHeaders })
    : req;

  return next(cloned).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        // No aplicar limpieza de sesion en estos casos:
        // 1. Ruta pública de contratos (/contracts/:id o /owner/contracts/:id)
        // 2. Endpoint de notificaciones (/v1/notifications/me) - puede ser llamado sin autenticación
        const currentPath = typeof location !== 'undefined' ? location.pathname : '';
        const requestUrl = err.url || '';
        const isPublicContractRoute = /^\/contracts\/[^\/]+$/.test(currentPath) || 
                                      /^\/owner\/contracts\/[^\/]+$/.test(currentPath) ||
                                      /\/v1\/contracts\/[^\/]+$/.test(requestUrl) ||
                                      /\/api\/v1\/contracts\/[^\/]+$/.test(requestUrl);
        const isNotificationsEndpoint = /\/v1\/notifications\/me/.test(requestUrl) ||
                                        /\/api\/v1\/notifications\/me/.test(requestUrl);
        const isContractsNotificationsEndpoint = /\/v1\/contracts\/notifications/.test(requestUrl) ||
                                                  /\/api\/v1\/contracts\/notifications/.test(requestUrl);
        
        if (!isPublicContractRoute && !isNotificationsEndpoint && !isContractsNotificationsEndpoint) {
          // Se limpia sesion, pero NO se redirige. La UI (bottom sheets/componentes)
          // decide como manejar errores de autenticacion.
          auth.logout();
        }
        // Si es una ruta publica o endpoint que puede fallar sin autenticacion,
        // simplemente dejar que el error se propague para que el componente pueda manejarlo.
      } else if (err.status === 0) {
        networkOfflineModal.show();
      } else if (err.status === 400 || err.status === 422) {
        const message =
          (typeof err.error === 'string' && err.error.trim()) ||
          err.error?.message ||
          'No pudimos procesar tu solicitud. Revisa la información e inténtalo nuevamente.';

        systemErrorModal.show({
          body: message,
        });
      } else if (err.status >= 500) {
        const defaultBody = 'Hubo un problema con el sistema. Por favor inténtalo de nuevo en unos minutos.';
        systemErrorModal.show({
          body: defaultBody,
        });
      }
      return throwError(() => err);
    })
  );
};
